import { AxiosInstance } from "axios";
import { stringify } from "query-string";
import { CrudFilters, DataProvider } from "@refinedev/core";
import { axiosInstance, generateSort, generateFilter } from "./utils";
import { GET_STREAM_TOKEN_KEY, TOKEN_KEY } from "./authProvider";
import { StreamChat } from 'stream-chat';
import { parseJwt } from "./utils/parseJwt";
// import { generateStreamToken } from "./utils/generateStreamToken";


type MethodTypes = "get" | "delete" | "head" | "options";
type MethodTypesWithBody = "post" | "put" | "patch";

const handleUnauthorized = () => {
  // Clear tokens
  localStorage.removeItem(TOKEN_KEY);
  localStorage.removeItem(GET_STREAM_TOKEN_KEY);
  // Redirect to login
  window.location.href = '/login';
};

export const dataProvider = (
  apiUrl: string,
  httpClient: AxiosInstance = axiosInstance
): Omit<
  Required<DataProvider>,
  "createMany" | "updateMany" | "deleteMany"
> => ({
  getList: async ({ resource, pagination, filters, sorters, meta }) => {
    const token = localStorage.getItem(TOKEN_KEY);
    const url = `${apiUrl}/${resource}`;

    const { current = 1, pageSize = 10, mode = "server" } = pagination ?? {};

    const { headers: headersFromMeta, method, q, userType } = meta ?? {};
    const requestMethod = (method as MethodTypes) ?? "get";

    const queryFilters = generateFilter(filters);

    const query: {
      _start?: number;
      _end?: number,
      _sort?: string,
      _order?: string;
      q?: string;
      userType?: ("ADMIN" | "USER")[];
    } = {};

    if (mode === "server") {
      query._start = (current - 1) * pageSize;
      query._end = current * pageSize;
    }

    if (q) {
      query.q = q;
    }

    if (userType) {
      query.userType = userType;
    }

    const generatedSort = generateSort(sorters);
    if (generatedSort) {
      const { _sort, _order } = generatedSort;
      query._sort = _sort.join(",");
      query._order = _order.join(",");
    }

    const { data, headers } = await httpClient[requestMethod](
      `${url}?${stringify(query)}&${stringify(queryFilters)}`,
      {
        headers: { ...headersFromMeta, 'Authorization': `Bearer ${token}` },
      }
    );

    const total = +(headers["X-Total-Count"] || 0);


    if (data.list) {
      return {
        data: data.list,
        total: total || data.total || data.list.length,
        meta: data.summary,
      };
    }

    return {
      data: data,
      total: total || data.length,
    }
  },

  getMany: async ({ resource, ids, meta }) => {
    const token = localStorage.getItem(TOKEN_KEY);

    const { headers, method, q } = meta ?? {};
    const requestMethod = (method as MethodTypes) ?? "get";
    const { data } = await httpClient[requestMethod](
      `${apiUrl}${resource}?${stringify({ id: ids })}`,
      {
        headers: { ...headers, Authorization: `Bearer ${token}` },
      }
    );
    return {
      data,
    };
  },

  create: async ({ resource, variables, meta }) => {
    const url = `${apiUrl}/${resource}`;

    const { headers, method } = meta ?? {};
    const requestMethod = (method as MethodTypesWithBody) ?? "post";

    const token = localStorage.getItem(TOKEN_KEY);

    const { data } = await httpClient[requestMethod](url, variables, {
      headers: {
        ...headers,
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
    })

    return {
      data,
    };
  },

  update: async ({ resource, id, variables, meta }) => {
    const token = localStorage.getItem(TOKEN_KEY);

    const url = `${apiUrl}/${resource}/${id}`;

    const { headers, method } = meta ?? {};
    const requestMethod = (method as MethodTypesWithBody) ?? "patch";


    const { data } = await httpClient[requestMethod](url, variables, {
      headers: {
        ...headers,
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
    });

    return {
      data,
    };
  },

  getOne: async ({ resource, id, meta }) => {

    const url = `${apiUrl}/${resource}/${id}`;

    const { headers, method } = meta ?? {};
    const requestMethod = (method as MethodTypes) ?? "get";

    const token = localStorage.getItem(TOKEN_KEY);
    const { data } = await httpClient[requestMethod](url, {
      headers: {
        ...headers,
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
    });

    return {
      data,
    };
  },

  deleteOne: async ({ resource, id, variables, meta }) => {
    const token = localStorage.getItem(TOKEN_KEY);
    const url = `${apiUrl}/${resource}/${id}`;
    const { headers, method } = meta ?? {};
    const requestMethod = (method as MethodTypesWithBody) ?? "delete";

    const { data } = await httpClient[requestMethod](url, {
      data: variables,
      headers: {
        ...headers,
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
    });

    return {
      data,
    };
  },

  getApiUrl: () => {
    return apiUrl;
  },

  custom: async ({
    url,
    method,
    filters,
    sorters,
    payload,
    query,
    headers,
  }) => {
    let requestUrl = `${url}?`;

    if (sorters) {
      const generatedSort = generateSort(sorters);
      if (generatedSort) {
        const { _sort, _order } = generatedSort;
        const sortQuery = {
          _sort: _sort.join(","),
          _order: _order.join(","),
        };
        requestUrl = `${requestUrl}&${stringify(sortQuery)}`;
      }
    }

    if (filters) {
      const filterQuery = generateFilter(filters);
      requestUrl = `${requestUrl}&${stringify(filterQuery)}`;
    }

    if (query) {
      requestUrl = `${requestUrl}&${stringify(query)}`;
    }

    const token = localStorage.getItem(TOKEN_KEY);

    let axiosResponse;
    switch (method) {
      case "put":
      case "post":
      case "patch":
        axiosResponse = await httpClient[method](url, payload, {
          headers: {
            ...headers,
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        });
        break;
      case "delete":
        axiosResponse = await httpClient.delete(url, {
          data: payload,
          headers: { ...headers, Authorization: `Bearer ${token}` },
        });
        break;
      default:
        axiosResponse = await httpClient.get(requestUrl, {
          headers: { ...headers, Authorization: `Bearer ${token}` },
        });
        break;
    }

    const { data } = axiosResponse;

    return Promise.resolve({ data });
  },
})

// Add response interceptor to handle 401/403 responses
axiosInstance.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401 || error.response?.status === 403) {
      handleUnauthorized();
    }
    return Promise.reject(error);
  }
);
